/**
 * Who invited this person to this rave — for the rave's own words (the user, 2026-10-27: the
 * Attend page says "<user> invited you to join <rave edition>"; signed out, the sign-in strip
 * says "Sign in to join <user> on <rave edition>"). From the invite this device arrived with
 * (`arrivedInvite`) and `GET /public/invites/:code` (a name and the rave's name, nothing more).
 * Null for any other rave, an unknown code, or no invite at all.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { InviteFromAnswer } from '@myrave/shared/publicSite';
import { arrivedInvite } from '@/lib/inviteTracking';

export interface InviteFrom { inviterName: string; editionId: string; editionName: string }

/** Who sent the invite this device arrived with — `GET /public/invites/:code`, asked once. */
function useArrivedInvite() {
  const code = arrivedInvite()?.code ?? null;
  return useQuery({
    queryKey: ['public', 'invite', code],
    queryFn: () => api.get<InviteFromAnswer>(`/public/invites/${encodeURIComponent(code!)}`),
    enabled: Boolean(code),
    staleTime: Infinity,
    retry: false,
  }).data;
}

/** `editionIds`: the editions this screen is about — one, or every year of the rave on screen. */
export function useInviteFrom(editionIds: readonly string[]): InviteFrom | null {
  const d = useArrivedInvite();
  return d && d.editionId && editionIds.includes(d.editionId) ? { inviterName: d.inviterName, editionId: d.editionId, editionName: d.editionName ?? '' } : null;
}

/** An invite to MyRave itself (step 89, no rave) this device arrived with — the main page names its sender. */
export function useMyRaveInviteFrom(): { inviterName: string } | null {
  const d = useArrivedInvite();
  return d && d.editionId === null ? { inviterName: d.inviterName } : null;
}
